import React, { ReactElement, useMemo, useState, useEffect } from "react";
import { Alert } from "react-bootstrap";
import ReactDom from "react-dom";
import { RouteComponentProps } from "react-router";
import { ProjectService } from ".";
import { DataTable } from "../ui/DataTable";
import { Project } from "./Project";

interface IParams {
	id?: string;
}

export const ProjectsTable = (
	props: RouteComponentProps<IParams>,
): ReactElement => {
	const [projects, setProjects] = useState<Project[]>();
	const portal = document.getElementById("account-project-portal");
	let output;

	if (!portal) {
		throw Error("#account-project-portal element could not be found");
	}

	const projectService = new ProjectService();

	function SelectColumnFilter({ column: { filterValue, setFilter } }: any) {
		const options = [
			"Not Started",
			"In Progress",
			"Completed",
			"On Hold",
			"Internal",
			"Support",
		];

		// Set default filter to exclude completed projects if no filter is set
		useEffect(() => {
			if (filterValue === undefined) {
				setFilter(["Not Started", "In Progress", "On Hold", "Internal", "Support"]);
			}
		}, [filterValue, setFilter]);

		return (
			<select
				value={Array.isArray(filterValue) ? filterValue.join(", ") : filterValue || ""}
				className="form-control"
				onChange={e => {
					const value = e.target.value;
					if (value.includes(",")) {
						// Handle comma-separated values
						setFilter(value.split(", "));
					} else if (value === "") {
						setFilter(undefined);
					} else {
						setFilter([value]);
					}
				}}
			>
				<option value="Not Started, In Progress, On Hold, Internal, Support">
					All (Excluding Completed)
				</option>
				<option value="Not Started, In Progress, Completed, On Hold, Internal, Support">
					All (Including Completed)
				</option>
				{options.map((option, i) => (
					<option key={i} value={option}>
						{option}
					</option>
				))}
			</select>
		);
	}

	// Custom filter function for multiple values
	const multiSelectFilter = (rows: any[], id: any, filterValue: any) => {
		if (!filterValue || !Array.isArray(filterValue)) {
			return rows;
		}
		return rows.filter(row => {
			const rowValue = row.values[id];
			return filterValue.includes(rowValue);
		});
	};

	const columns = [
		{
			Header: "ID",
			accessor: "id",
			width: "10%",
		},
		{
			Header: "Name",
			accessor: "name",
			Cell: ({ row: { original } }: any) => (
				<a href={`/projects/${original.id}`}>{original.name}</a>
			),
			width: "50%",
		},
		{
			Header: "Status",
			accessor: "status",
			Filter: SelectColumnFilter,
			filter: multiSelectFilter,
			width: "20%",
		},
		{
			Header: "Budget",
			accessor: "budget",
			Cell: ({ cell: { value } }: any) => <>${value}</>,
			Filter: false,
			width: "10%",
		},
	];

	useMemo(() => {
		const fetchProject = async () => {
			const projectData = await projectService.findByAccount!(
				props.match.params.id || "",
			);
			setProjects(projectData);
		};

		fetchProject();
	}, []);

	if (!projects || !projects.length) {
		output = (
			<Alert variant="info">
				No projects have been added to this account.
			</Alert>
		);
	} else {
		output = <DataTable columns={columns} data={projects} />;
	}

	return ReactDom.createPortal(output, portal);
};
